import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Pencil, Check, X, ShoppingCart, PackageCheck, Minus, ListPlus, Receipt, Scale, Store, AlertCircle, Users, Star, Copy, ArrowLeft, ShoppingBag, ChevronRight, CheckSquare, Square, RotateCcw, Heart, MapPin, Navigation } from 'lucide-react';
import { axiosInstance, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const STATUSES = ['have', 'low', 'need'];
const STATUS_STYLE = {
  have: 'bg-emerald-100 text-emerald-700',
  low: 'bg-amber-100 text-amber-700',
  need: 'bg-rose-100 text-rose-700',
};

// Type-ahead product suggestions (pure DB lookup, no AI). Debounced.
const useProductSuggestions = (query) => {
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    const q = (query || '').trim();
    if (q.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    const tmr = setTimeout(async () => {
      try {
        const res = await axiosInstance.get('/price/suggest', { params: { q } });
        if (!cancelled) setSuggestions(res.data?.suggestions || []);
      } catch { if (!cancelled) setSuggestions([]); }
    }, 180);
    return () => { cancelled = true; clearTimeout(tmr); };
  }, [query]);
  return suggestions;
};

// Input with a tap-to-select suggestions dropdown. Mobile-friendly, RTL-safe.
export const ProductSuggestInput = ({ value, onChange, onSelect, onEnter, placeholder, testid }) => {
  const [focused, setFocused] = useState(false);
  const suggestions = useProductSuggestions(value);
  const show = focused && suggestions.length > 0;
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        onKeyDown={(e) => { if (e.key === 'Enter') { setFocused(false); onEnter?.(); } }}
        placeholder={placeholder}
        className="h-10 text-sm"
        autoComplete="off"
        data-testid={testid}
      />
      {show && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto" data-testid={`${testid}-suggestions`}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onSelect(s); setFocused(false); }}
              className="w-full text-start px-3 py-2 hover:bg-indigo-50 transition-colors flex flex-col border-b last:border-b-0"
              data-testid={`${testid}-suggestion-${i}`}
            >
              <span className="text-sm text-gray-900 truncate">{s.name}</span>
              {(s.brand || s.packSize) && (
                <span className="text-[11px] text-gray-400 truncate">{[s.brand, s.packSize].filter(Boolean).join(' · ')}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SignInPrompt = ({ label }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl bg-white border p-6 text-center" data-testid="price-shop-signin">
      <p className="text-sm text-gray-600 mb-3">{label}</p>
      <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate('/signin')} data-testid="price-shop-signin-btn">
        {t('price.signInCta')}
      </Button>
    </div>
  );
};

// Sponsored promotions strip — clearly-marked paid offers relevant to the
// given product terms. NEVER part of price comparison. Reused in Shopping List
// and beside Compare results.
export const SponsoredStrip = ({ terms = [], town = '', testid = 'sponsored-strip' }) => {
  const { t } = useTranslation();
  const [promos, setPromos] = useState([]);
  const sid = () => {
    let s = localStorage.getItem('f2d_session');
    if (!s) { s = 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('f2d_session', s); }
    return s;
  };
  const key = terms.filter((x) => x && x.length >= 2).slice(0, 4).join('|');
  useEffect(() => {
    const list = key.split('|').filter(Boolean);
    if (list.length === 0) { setPromos([]); return; }
    let cancelled = false;
    (async () => {
      const seen = {}; const out = [];
      for (const term of list) {
        try {
          const r = await axiosInstance.get('/price/promotions/search', { params: { q: term, town } });
          for (const p of (r.data?.promotions || [])) { if (!seen[p.id]) { seen[p.id] = 1; out.push(p); } }
        } catch { /* ignore */ }
        if (out.length >= 4) break;
      }
      if (!cancelled) {
        setPromos(out.slice(0, 4));
        out.slice(0, 4).forEach((p) => axiosInstance.post(`/price/promotions/${p.id}/view`, { sessionId: sid() }).catch(() => {}));
      }
    })();
    return () => { cancelled = true; };
  }, [key, town]);
  if (promos.length === 0) return null;
  const tap = (p) => { axiosInstance.post(`/price/promotions/${p.id}/click`, { sessionId: sid() }).catch(() => {}); if (p.businessId) window.open(`/business/${p.businessId}`, '_blank'); };
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{t('pricePromo.sponsored', 'Sponsored')}</span>
        <span className="text-[11px] text-gray-400">{t('pricePromo.sponsoredHint', 'Paid offer — not part of the price comparison')}</span>
      </div>
      {promos.map((p, i) => (
        <button key={p.id} onClick={() => tap(p)} className="w-full text-start rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 p-2.5 flex gap-2 hover:bg-amber-50 transition-colors" data-testid={`${testid}-card-${i}`}>
          {p.photoUrl && <img src={p.photoUrl} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-gray-900 truncate">{p.productName}</p>
            <p className="text-[11px] text-gray-500 truncate">{p.shopName}{p.town ? ` · ${p.town}` : ''}</p>
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-bold text-amber-700">{money(p.promoPrice, p.currency)}</span>
              {p.normalPrice != null && <span className="text-[11px] text-gray-400 line-through">{money(p.normalPrice, p.currency)}</span>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

// ----- Shared "Add to which list?" destination picker + add helper -----
// Reuses the EXISTING lists + items endpoints. Only lists the caller can EDIT
// are offered (owned personal/named lists, Today's Shopping, and shared lists
// the caller can write to) — never anyone else's lists.
export const addItemsToListApi = async (listId, items) => {
  let added = 0, merged = 0;
  for (const it of items) {
    try {
      const r = await axiosInstance.post(`/price/lists/${listId}/items`, {
        name: it.name, qty: it.qty || 1, normalizedName: it.normalizedName || null,
        brand: it.brand || null, packSize: it.packSize || null,
      });
      if (r.data?.outcome === 'merged') merged += 1; else added += 1;
    } catch { /* skip a single failed item, continue */ }
  }
  return { added, merged };
};

const DestRow = ({ id, label, shared, onPick }) => {
  const { t } = useTranslation();
  return (
    <button onClick={() => onPick(id, label)} className="w-full text-start rounded-xl bg-white border p-3 flex items-center gap-2 hover:bg-slate-50 transition-colors" data-testid={`list-picker-dest-${id}`}>
      <ShoppingBag className="h-4 w-4 text-indigo-500 shrink-0" />
      <span className="flex-1 text-sm text-gray-900 truncate">{label}</span>
      {shared && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 shrink-0">{t('listShare.kind_direct', 'Shared')}</span>}
    </button>
  );
};

export const ListPickerSheet = ({ open, onClose, onPick, excludeId }) => {
  const { t } = useTranslation();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  useEffect(() => {
    if (!open) return;
    setLoading(true); setCreating(false); setNewName('');
    axiosInstance.get('/price/lists')
      .then((r) => setLists((r.data?.lists || []).filter((l) => l.canEdit !== false && l.id !== excludeId)))
      .catch(() => setLists([]))
      .finally(() => setLoading(false));
  }, [open, excludeId]);
  if (!open) return null;
  const today = lists.find((l) => l.isToday);
  const named = lists.filter((l) => !l.isToday && l.kind === 'personal');
  const shared = lists.filter((l) => !l.isToday && l.kind !== 'personal');
  const createAndPick = async () => {
    const nm = newName.trim();
    if (!nm) return;
    try { const r = await axiosInstance.post('/price/lists', { kind: 'personal', name: nm }); onPick(r.data.id, nm); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40" data-testid="list-picker-sheet" onClick={onClose}>
      <div className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl p-4 space-y-2 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-bold text-gray-900">{t('listShare.addToWhich', 'Add to…')}</p>
          <button onClick={onClose} className="text-gray-400 p-1" aria-label={t('price.back', 'Close')} data-testid="list-picker-close"><X className="h-5 w-5" /></button>
        </div>
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-600 mx-auto" /></div>
        ) : (
          <div className="space-y-1.5">
            {today && <DestRow id={today.id} label={t('listShare.today', "Today's Shopping")} onPick={onPick} />}
            {named.map((l) => <DestRow key={l.id} id={l.id} label={l.name} onPick={onPick} />)}
            {shared.map((l) => <DestRow key={l.id} id={l.id} label={l.name} shared onPick={onPick} />)}
            {creating ? (
              <div className="flex gap-2 pt-1">
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('listShare.namePh', 'List name')} className="h-9 text-sm" data-testid="list-picker-newname" />
                <Button className="rounded-full bg-indigo-600 h-9" onClick={createAndPick} data-testid="list-picker-newcreate">{t('listShare.create', 'Create')}</Button>
              </div>
            ) : (
              <button onClick={() => setCreating(true)} className="w-full text-start rounded-xl border border-dashed border-indigo-300 p-3 text-indigo-700 text-sm inline-flex items-center gap-1" data-testid="list-picker-new"><Plus className="h-4 w-4" />{t('listShare.newList', 'New List')}</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ---------------------------- MY PRODUCTS ----------------------------
export const MyProductsPanel = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [packSize, setPackSize] = useState('');
  const [selectedNorm, setSelectedNorm] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [showReceipts, setShowReceipts] = useState(false);
  const [favKeys, setFavKeys] = useState(() => new Set());
  const [sel, setSel] = useState({});
  const [pickOpen, setPickOpen] = useState(false);
  const [pickItems, setPickItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/price/my-products');
      setProducts(res.data?.products || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const loadFav = useCallback(async () => {
    try { const r = await axiosInstance.get('/price/favourites/keys'); setFavKeys(new Set(r.data?.keys || [])); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { if (user) { load(); loadFav(); } else setLoading(false); }, [user, load, loadFav]);

  const add = async () => {
    if (!name.trim()) { toast.error(t('price.mpNameRequired')); return; }
    setAdding(true);
    try {
      const res = await axiosInstance.post('/price/my-products', { name: name.trim(), normalizedName: selectedNorm || null, brand: brand.trim() || null, packSize: packSize.trim() || null, status: 'have' });
      if (!res.data?.created) toast.info(t('price.mpDuplicate'));
      else toast.success(t('price.mpAdded'));
      setName(''); setBrand(''); setPackSize(''); setSelectedNorm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); } finally { setAdding(false); }
  };

  const setStatus = async (id, status) => {
    try { await axiosInstance.patch(`/price/my-products/${id}`, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); }
  };

  const openPickFor = (prods) => {
    setPickItems(prods.map((p) => ({ name: p.name, normalizedName: p.normalizedName, brand: p.brand, packSize: p.packSize, qty: 1, _id: p.id })));
    setPickOpen(true);
  };
  const onPickDest = async (listId, label) => {
    setPickOpen(false);
    const res = await addItemsToListApi(listId, pickItems);
    for (const it of pickItems) { if (it._id) axiosInstance.patch(`/price/my-products/${it._id}`, { status: 'need' }).catch(() => {}); }
    toast.success(t('listShare.addedToList', { n: res.added + res.merged, list: label, defaultValue: 'Added {{n}} to {{list}}' }));
    setSel({}); load();
  };
  const selProducts = () => products.filter((p) => sel[p.id]);
  const selCount = Object.keys(sel).filter((k) => sel[k]).length;

  const saveEdit = async (id) => {
    if (!editName.trim()) { toast.error(t('price.mpNameRequired')); return; }
    try { await axiosInstance.patch(`/price/my-products/${id}`, { name: editName.trim() }); setEditId(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); }
  };

  const remove = async (id) => {
    try { await axiosInstance.delete(`/price/my-products/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); }
  };

  // Favourite heart — reuses the EXISTING favourites system (no new store).
  // Does NOT touch My Products, status, or Today's Shopping.
  const favKeyOf = (p) => `${(p.name || '').trim().toLowerCase()}|${(p.brand || '').trim().toLowerCase()}|${(p.packSize || '').trim().toLowerCase()}`;
  const toggleFav = async (p) => {
    const key = favKeyOf(p);
    setFavKeys((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });  // optimistic
    try {
      const r = await axiosInstance.post('/price/favourites/toggle', { name: p.name, normalizedName: p.normalizedName || null, brand: p.brand || null, packSize: p.packSize || null });
      toast.success(r.data?.favourited ? t('listShare.favAdded', 'Added to Favourites') : t('listShare.favRemoved', 'Removed from Favourites'));
    } catch { loadFav(); toast.error(t('price.errGeneric')); }
  };

  if (!user) return <SignInPrompt label={t('price.mpSignIn')} />;

  return (
    <div className="space-y-4" data-testid="price-myproducts">
      {/* Add form */}
      <div className="rounded-2xl bg-white border p-4 space-y-2">
        <p className="text-sm font-semibold text-gray-800">{t('price.mpAddTitle')}</p>
        <ProductSuggestInput
          value={name}
          onChange={(v) => { setName(v); setSelectedNorm(null); }}
          onSelect={(s) => { setName(s.name); setBrand(s.brand || ''); setPackSize(s.packSize || ''); setSelectedNorm(s.normalizedName || null); }}
          placeholder={t('price.mpNamePh')}
          testid="mp-name-input"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={t('price.mpBrandPh')} className="h-10 text-sm" data-testid="mp-brand-input" />
          <Input value={packSize} onChange={(e) => setPackSize(e.target.value)} placeholder={t('price.mpPackPh')} className="h-10 text-sm" data-testid="mp-pack-input" />
        </div>
        <div className="flex gap-2">
          <Button className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={add} disabled={adding} data-testid="mp-add-button">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />{t('price.mpAddBtn')}</>}
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => setShowReceipts((s) => !s)} data-testid="mp-from-receipt-toggle">
            <Receipt className="h-4 w-4 mr-1" />{t('price.mpFromReceipt')}
          </Button>
        </div>
      </div>

      {showReceipts && <ReceiptPicker onDone={() => { setShowReceipts(false); load(); }} />}

      {/* List */}
      {loading ? (
        <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto" /></div>
      ) : products.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8" data-testid="mp-empty">{t('price.mpEmpty')}</p>
      ) : (
        <div className="space-y-2" data-testid="mp-list">
          {products.map((p) => (
            <div key={p.id} className="rounded-xl bg-white border p-3" data-testid={`mp-item-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <button onClick={() => setSel((s) => ({ ...s, [p.id]: !s[p.id] }))} className="shrink-0 text-indigo-600 mt-0.5" aria-label={t('listShare.select', 'Select')} data-testid={`mp-select-${p.id}`}>
                  {sel[p.id] ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-gray-300" />}
                </button>
                <div className="min-w-0 flex-1">
                  {editId === p.id ? (
                    <div className="flex items-center gap-1">
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 text-sm" data-testid={`mp-edit-input-${p.id}`} />
                      <button onClick={() => saveEdit(p.id)} className="text-emerald-600 p-1" data-testid={`mp-edit-save-${p.id}`}><Check className="h-4 w-4" /></button>
                      <button onClick={() => setEditId(null)} className="text-gray-400 p-1"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-gray-900 truncate" data-testid={`mp-name-${p.id}`}>{p.name}</p>
                  )}
                  <p className="text-[11px] text-gray-400 truncate">{[p.brand, p.packSize].filter(Boolean).join(' · ')}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggleFav(p)} className="p-1" aria-label={t('listShare.favourite', 'Favourite')} data-testid={`mp-fav-${p.id}`}>
                    <Heart className={`h-3.5 w-3.5 transition-colors ${favKeys.has(favKeyOf(p)) ? 'text-rose-500 fill-rose-500' : 'text-gray-300 hover:text-rose-400'}`} />
                  </button>
                  <button onClick={() => { setEditId(p.id); setEditName(p.name); }} className="text-gray-400 hover:text-indigo-600 p-1" aria-label={t('price.edit')} data-testid={`mp-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(p.id)} className="text-gray-400 hover:text-red-600 p-1" aria-label={t('price.delete')} data-testid={`mp-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                {STATUSES.map((s) => (
                  <button key={s} onClick={() => setStatus(p.id, s)} className={`text-[11px] px-2 py-1 rounded-full transition-colors ${p.status === s ? STATUS_STYLE[s] : 'bg-gray-100 text-gray-400'}`} data-testid={`mp-status-${s}-${p.id}`}>
                    {t(`price.status_${s}`)}
                  </button>
                ))}
                <button onClick={() => openPickFor([p])} className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-indigo-600 text-white inline-flex items-center gap-1" data-testid={`mp-tolist-${p.id}`}>
                  <ListPlus className="h-3 w-3" />{t('price.mpToList')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {selCount > 0 && (
        <div className="sticky bottom-2 z-10" data-testid="mp-selection-bar">
          <Button className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 h-11 shadow-lg" onClick={() => openPickFor(selProducts())} data-testid="mp-add-selected-tolist">
            <ListPlus className="h-4 w-4 mr-1.5" />{t('listShare.addToListN', { n: selCount, defaultValue: 'Add to list ({{n}})' })}
          </Button>
        </div>
      )}
      <ListPickerSheet open={pickOpen} onClose={() => setPickOpen(false)} onPick={onPickDest} />
    </div>
  );
};

// Receipt picker for "Add from receipt" (My Products)
const ReceiptPicker = ({ onDone }) => {
  const { t } = useTranslation();
  const [receipts, setReceipts] = useState(null);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosInstance.get('/price/receipts/mine').then((r) => setReceipts(r.data?.receipts || [])).catch(() => setReceipts([]));
  }, []);

  const openReceipt = async (id) => {
    try {
      const r = await axiosInstance.get(`/price/receipts/mine/${id}`);
      setActive(id); setItems(r.data?.items || []);
      const c = {}; (r.data?.items || []).forEach((_, i) => { c[i] = true; }); setChecked(c);
    } catch { toast.error(t('price.errGeneric')); }
  };

  const addSelected = async () => {
    const sel = items.filter((_, i) => checked[i]).map((it) => ({
      name: it.normalizedName || it.description, normalizedName: it.normalizedName || null, brand: it.brand || null, packSize: it.packSize || null,
    }));
    if (sel.length === 0) { toast.error(t('price.mpPickOne')); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.post('/price/my-products/from-receipt', { receiptId: active, items: sel });
      toast.success(t('price.mpFromReceiptDone', { added: res.data?.added || 0, skipped: res.data?.skippedDuplicates || 0 }));
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl bg-white border p-4" data-testid="mp-receipt-picker">
      {receipts === null ? (
        <div className="text-center py-4"><Loader2 className="h-5 w-5 animate-spin text-indigo-600 mx-auto" /></div>
      ) : receipts.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-2" data-testid="mp-receipt-empty">{t('price.mpNoReceipts')}</p>
      ) : !active ? (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-800">{t('price.mpPickReceipt')}</p>
          {receipts.map((r) => (
            <button key={r.id} onClick={() => openReceipt(r.id)} className="w-full text-left rounded-lg border p-2 hover:bg-slate-50" data-testid={`mp-receipt-${r.id}`}>
              <span className="text-sm text-gray-800 font-medium">{r.shopName || t('price.unknownShop')}</span>
              <span className="text-[11px] text-gray-400 block">{r.receiptDate || '—'} · {t('price.itemsCount', { count: r.itemCount })}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-800">{t('price.mpTickItems')}</p>
          <div className="max-h-64 overflow-auto space-y-1">
            {items.map((it, i) => (
              <label key={i} className="flex items-center gap-2 py-1 text-sm" data-testid={`mp-receipt-item-${i}`}>
                <input type="checkbox" checked={!!checked[i]} onChange={(e) => setChecked((c) => ({ ...c, [i]: e.target.checked }))} className="h-4 w-4" data-testid={`mp-receipt-check-${i}`} />
                <span className="truncate">{it.normalizedName || it.description}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 pt-1">
            <Button className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={addSelected} disabled={saving} data-testid="mp-receipt-add">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('price.mpAddSelected')}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => setActive(null)}>{t('price.back')}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------- COMPARE BASKET ----------------------------
const money = (v, cur) => (v == null ? '—' : `${cur ? cur + ' ' : '€'}${Number(v).toFixed(2)}`);

const CompareBasket = ({ hasItems, terms = [], listId = '', label = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [mode, setMode] = useState('cheapest');
  const [towns, setTowns] = useState([]);
  const [town, setTown] = useState(() => localStorage.getItem('f2d_price_town') || '');
  const [view, setView] = useState('list');

  useEffect(() => { axiosInstance.get('/price/towns').then((r) => setTowns(r.data?.towns || [])).catch(() => {}); }, []);

  const run = useCallback(async () => {
    setLoading(true); setOpen(true);
    try {
      const q = new URLSearchParams();
      if (listId) q.set('listId', listId);
      if (town) q.set('fromTown', town);
      const qs = q.toString();
      const res = await axiosInstance.post(`/price/compare-basket${qs ? `?${qs}` : ''}`);
      setData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); setOpen(false); }
    finally { setLoading(false); }
  }, [listId, town, t]);

  // recompute distances when the user changes their town (results already open)
  useEffect(() => { if (open) run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [town]);

  const changeTown = (v) => { setTown(v); if (v) localStorage.setItem('f2d_price_town', v); else localStorage.removeItem('f2d_price_town'); };
  const dirUrl = (g) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${g.shopName || ''}${g.town ? `, ${g.town}` : (g.shopBranch ? `, ${g.shopBranch}` : '')}`)}`;

  const ShopLoc = ({ g }) => (
    g.locationStatus === 'ok' ? (
      <div className="mt-1 flex items-center gap-3 text-[12px]">
        <span className="inline-flex items-center gap-1 text-gray-500" data-testid="cmp-shop-dist"><MapPin className="h-3.5 w-3.5" />{g.distanceKm != null ? `~${g.distanceKm} km` : g.town}</span>
        <a href={dirUrl(g)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-indigo-600 font-medium" data-testid="cmp-shop-directions"><Navigation className="h-3.5 w-3.5" />{t('price.dist.directions', 'Directions')}</a>
      </div>
    ) : (
      <p className="mt-1 text-[12px] text-gray-400 inline-flex items-center gap-1" data-testid="cmp-shop-noloc"><MapPin className="h-3.5 w-3.5" />{t('price.dist.unavailable', 'Location not yet available')}</p>
    )
  );

  const renderShops = (shops) => (shops || []).map((g, i) => (
    <div key={i} className="rounded-xl border bg-white p-3" data-testid={`cmp-shop-${i}`}>
      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><Store className="h-4 w-4 text-indigo-600" />{g.shopName}{g.isDemo && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">DEMO</span>}</p>
      <ShopLoc g={g} />
      <div className="mt-1.5 space-y-1">
        {g.lines.map((l, j) => (
          <div key={j} className="flex items-center justify-between text-[13px]" data-testid={`cmp-line-${i}-${j}`}>
            <span className="text-gray-700 truncate">{l.name}{l.qty > 1 ? ` ×${l.qty}` : ''}</span>
            <span className="text-gray-900 font-medium tabular-nums">{money(l.linePrice, l.currency)}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 pt-1.5 border-t flex items-center justify-between text-[13px] font-semibold">
        <span className="text-gray-500">{t('price.cmpSubtotal')}</span>
        <span className="text-gray-900 tabular-nums">{money(g.subtotal, g.lines[0]?.currency)}</span>
      </div>
    </div>
  ));

  let opt = null, total = null, shops = null;
  if (data && !data.empty) {
    if (mode === 'one') { opt = data.options.oneShop; shops = opt ? [opt] : []; total = opt?.subtotal; }
    else if (mode === 'two') { opt = data.options.twoShop; shops = opt?.shops || []; total = opt?.total; }
    else if (mode === 'nearest') { opt = data.options.nearest; shops = opt ? [opt] : []; total = opt?.subtotal; }
    else { opt = data.options.cheapest; shops = opt?.shops || []; total = opt?.total; }
  }

  return (
    <div data-testid="price-compare">
      <Button className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 h-11" onClick={run} disabled={!hasItems || loading} data-testid="compare-button">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Scale className="h-4 w-4 mr-2" />{label || t('price.cmpButton')}</>}
      </Button>

      {open && data && (
        <div className="mt-3 rounded-2xl bg-slate-50 border p-3 space-y-3" data-testid="compare-results">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-gray-900">{t('price.cmpTitle')}</p>
            <button onClick={() => setOpen(false)} className="text-gray-400" data-testid="compare-close"><X className="h-4 w-4" /></button>
          </div>

          {data.empty ? (
            <p className="text-sm text-gray-500 py-4 text-center">{t('price.slEmpty')}</p>
          ) : (
            <>
              {/* town selector + List | Map (Map = Stage 2, coming soon) */}
              <div className="flex items-center gap-2">
                <select value={town} onChange={(e) => changeTown(e.target.value)} className="flex-1 h-9 px-2 rounded-xl border text-[12px] bg-white" data-testid="compare-town">
                  <option value="">{t('price.dist.pick', 'Select your town to see distances')}</option>
                  {towns.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
                </select>
                <div className="flex rounded-full bg-gray-200 p-0.5 text-[11px] shrink-0">
                  <button onClick={() => setView('list')} className={`rounded-full px-3 py-1 ${view === 'list' ? 'bg-white shadow text-emerald-700' : 'text-gray-500'}`} data-testid="compare-view-list">{t('price.dist.list', 'List')}</button>
                  <button disabled title={t('price.dist.soon', 'Coming soon')} className="rounded-full px-3 py-1 text-gray-400 opacity-60 cursor-not-allowed" data-testid="compare-view-map">{t('price.dist.map', 'Map')} · {t('price.dist.soon', 'Coming soon')}</button>
                </div>
              </div>

              {/* mode toggle */}
              <div className="grid grid-cols-4 rounded-full bg-gray-200 p-1 text-[12px] font-medium">
                {[['one', t('price.cmpOneShop')], ['two', t('price.cmpTwoShops')], ['cheapest', t('price.cmpCheapest')], ['nearest', t('price.cmpNearest', 'Nearest')]].map(([k, label]) => (
                  <button key={k} onClick={() => setMode(k)} className={`rounded-full py-1.5 transition-colors ${mode === k ? 'bg-white shadow text-emerald-700' : 'text-gray-500'}`} data-testid={`compare-mode-${k}`}>{label}</button>
                ))}
              </div>

              {/* total + saving */}
              <div className="rounded-xl bg-white border p-3 text-center">
                <p className="text-[11px] text-gray-500 uppercase tracking-wide">{t('price.cmpEstTotal')}</p>
                <p className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="compare-total">{money(total, data.currency)}</p>
                {mode !== 'one' && data.savings && data.savings.saving > 0 && (
                  <p className="text-[13px] text-emerald-600 mt-1" data-testid="compare-saving">{t('price.cmpSaving')}: {money(data.savings.saving, data.currency)}</p>
                )}
                {data.distanceEnabled && opt && opt.approxKm != null && (
                  <p className="text-[12px] text-gray-500 mt-1" data-testid="compare-approx-km">{t('price.dist.approxTravel', 'Approx. travel')}: ~{opt.approxKm} km{opt.kmComplete ? '' : ` · ${t('price.dist.partial', 'Some shop locations not yet available')}`}</p>
                )}
                {data.distanceEnabled && mode !== 'one' && data.options.oneShop && data.options.oneShop.approxKm != null && opt && opt.approxKm != null && (
                  <p className="text-[12px] text-gray-600 mt-1 bg-slate-50 rounded-lg px-2 py-1" data-testid="compare-distance-aware">
                    {t('price.dist.productSaving', 'Product saving')}: ~{money((data.savings && data.savings.saving) || 0, data.currency)} · {t('price.dist.extraTravel', 'Approx. extra travel')}: ~{Math.round((opt.approxKm - data.options.oneShop.approxKm) * 10) / 10} km ({t('price.dist.vsOne', 'vs One Shop')})
                  </p>
                )}
                {mode === 'nearest' && opt && (
                  <p className="text-[12px] text-gray-600 mt-1" data-testid="compare-nearest-covers">
                    {t('price.dist.knownFor', { covered: opt.covered, total: data.listItemCount, defaultValue: 'Known recent prices for {{covered}}/{{total}} items' })}
                  </p>
                )}
              </div>

              {mode === 'nearest' && !opt ? (
                <p className="text-sm text-gray-500 text-center py-3" data-testid="compare-nearest-none">
                  {data.distanceEnabled ? t('price.dist.nearestNone', 'No reliably-located shop for these items yet') : t('price.dist.pick', 'Select your town to see distances')}
                </p>
              ) : shops && shops.length > 0 ? <div className="space-y-2">{renderShops(shops)}</div>
                : <p className="text-sm text-gray-500 text-center py-3">{t('price.cmpNoData')}</p>}

              {/* coverage */}
              <p className="text-[12px] text-gray-500 text-center" data-testid="compare-coverage">
                {t('price.cmpCoverage', { found: data.pricedItemCount, total: data.listItemCount })}
              </p>

              {/* missing */}
              {data.missing && data.missing.length > 0 && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5" data-testid="compare-missing">
                  <p className="text-[12px] font-semibold text-amber-700 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />{t('price.cmpNoRecent')}</p>
                  <p className="text-[12px] text-amber-700 mt-0.5">{data.missing.map((m) => m.name).join(', ')}</p>
                </div>
              )}

              <p className="text-[11px] text-gray-400 text-center italic">{t('price.cmpDisclaimer')}</p>

              {/* Sponsored offers relevant to this basket — separate, never in totals */}
              <SponsoredStrip terms={terms} testid="compare-sponsored" />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------- SHOPPING LISTS ----------------------------
// General-purpose, category-neutral lists. "Today's Shopping" is the one active
// trip (a personal list flagged isToday). Named lists (Weekly Groceries, DIY,
// Logs, Pets, gardening, car supplies — anything) can each be Personal /
// Household(calendar) / Direct-Shared, and feed selected items into Today's.
const KIND_BADGE = {
  personal: { label: 'Personal', cls: 'bg-gray-100 text-gray-500' },
  calendar: { label: 'Household', cls: 'bg-violet-100 text-violet-700' },
  direct: { label: 'Shared', cls: 'bg-indigo-100 text-indigo-700' },
};

export const ShoppingListPanel = ({ initialListId = null, autoToday = false, onManageProducts = null }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lists, setLists] = useState([]);
  const [openId, setOpenId] = useState(null);        // null = lists home
  const appliedInitial = useRef(false);
  const [items, setItems] = useState([]);
  const [canEdit, setCanEdit] = useState(true);
  const [selected, setSelected] = useState({});      // itemId -> bool (copy picker)
  const [name, setName] = useState('');
  const [selectedSug, setSelectedSug] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [members, setMembers] = useState([]);
  const [listPickOpen, setListPickOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMode, setNewMode] = useState('personal');
  const [cals, setCals] = useState([]);
  const [newCal, setNewCal] = useState('');
  const [showRepeat, setShowRepeat] = useState(false);
  const [lastShop, setLastShop] = useState(null);
  const [repeatSel, setRepeatSel] = useState({});
  const [regulars, setRegulars] = useState([]);   // favourites + saved products for one-tap quick add
  const autoTodayRef = useRef(false);
  const verRef = useRef(0);

  const todayList = lists.find((l) => l.isToday);
  const otherLists = lists.filter((l) => !l.isToday);
  const openList = lists.find((l) => l.id === openId) || null;
  const isTodayOpen = !!(openList && openList.isToday);

  const loadLists = useCallback(async () => {
    try { const r = await axiosInstance.get('/price/lists'); setLists(r.data?.lists || []); } catch { /* ignore */ }
  }, []);

  const loadItems = useCallback(async (id, silent) => {
    if (!id) return;
    if (!silent) setLoading(true);
    try {
      const r = await axiosInstance.get(`/price/lists/${id}/items`);
      if (silent && r.data?.version === verRef.current) return;   // unchanged, cheap
      verRef.current = r.data?.version || 0;
      setItems(r.data?.items || []); setCanEdit(r.data?.canEdit !== false);
    } catch (e) { if (e?.response?.status === 404) { setOpenId(null); loadLists(); } }
    finally { if (!silent) setLoading(false); }
  }, [loadLists]);

  const loadMembers = useCallback(async () => {
    if (!openId) { setMembers([]); return; }
    try { const r = await axiosInstance.get(`/price/lists/${openId}/members`); setMembers(r.data?.members || []); }
    catch { setMembers([]); }
  }, [openId]);

  useEffect(() => { if (user) loadLists().finally(() => setLoading(false)); else setLoading(false); }, [user, loadLists]);

  // Load the user's "things I buy often" (favourites first, then saved regular
  // products) for one-tap quick-add on Today's Shopping. Dedupe by name+pack.
  useEffect(() => {
    if (!user) { setRegulars([]); return; }
    let alive = true;
    (async () => {
      try {
        const [f, m] = await Promise.all([
          axiosInstance.get('/price/favourites').catch(() => ({ data: {} })),
          axiosInstance.get('/price/my-products').catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        const seen = new Set();
        const out = [];
        const push = (name, brand, packSize, normalizedName) => {
          const nm = (name || '').trim();
          if (!nm) return;
          const key = `${nm.toLowerCase()}|${(brand || '').trim().toLowerCase()}|${(packSize || '').trim().toLowerCase()}`;
          if (seen.has(key)) return;
          seen.add(key);
          out.push({ name: nm, brand: brand || null, packSize: packSize || null, normalizedName: normalizedName || null });
        };
        (f.data?.favourites || []).forEach((x) => push(x.name, x.brand, x.packSize, x.normalizedName));
        (m.data?.products || m.data?.myProducts || []).forEach((x) => push(x.name, x.brand, x.packSize, x.normalizedName));
        setRegulars(out.slice(0, 12));
      } catch { if (alive) setRegulars([]); }
    })();
    return () => { alive = false; };
  }, [user]);

  // Nan-friendly: open straight into Today's Shopping (unless a specific shared
  // list was deep-linked). Applied once, after lists have loaded.
  useEffect(() => {
    if (!autoToday || autoTodayRef.current || initialListId || openId || !lists.length) return;
    const today = lists.find((l) => l.isToday);
    if (today) { setOpenId(today.id); autoTodayRef.current = true; }
  }, [autoToday, initialListId, openId, lists]);

  // Deep-link: when arriving from a "shopping list shared with you" notification,
  // auto-open that specific list once the lists are loaded (applied once).
  useEffect(() => {
    if (!initialListId || appliedInitial.current || !lists.length) return;
    if (lists.some((l) => l.id === initialListId)) {
      setOpenId(initialListId);
      appliedInitial.current = true;
    }
  }, [initialListId, lists]);

  useEffect(() => {
    if (!openId) { setItems([]); setSelected({}); return; }
    verRef.current = -1; setSelected({}); loadItems(openId);
  }, [openId, loadItems]);

  useEffect(() => { loadMembers(); }, [openId, loadMembers]);

  // lightweight polling + refocus refresh (only while a list is open)
  useEffect(() => {
    if (!openId) return;
    const iv = setInterval(() => loadItems(openId, true), 6500);
    const onFocus = () => loadItems(openId, true);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [openId, loadItems]);

  useEffect(() => {
    if (showCreate && newMode === 'calendar' && cals.length === 0) {
      axiosInstance.get('/calendar/calendars').then((r) => setCals(r.data?.calendars || [])).catch(() => setCals([]));
    }
  }, [showCreate, newMode, cals.length]);

  const active = items.filter((i) => !i.checked);
  const completed = items.filter((i) => i.checked);
  // Today's Shopping displays items alphabetically (A–Z) by name. Display-only:
  // does not affect quantities, checked status, comparison, or other lists.
  const byNameAZ = (a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' });
  const activeAZ = [...active].sort(byNameAZ);
  const completedAZ = [...completed].sort(byNameAZ);

  const add = async () => {
    if (!name.trim()) { toast.error(t('price.slNameRequired')); return; }
    setAdding(true);
    try {
      await axiosInstance.post(`/price/lists/${openId}/items`, {
        name: name.trim(), qty: 1, normalizedName: selectedSug?.normalizedName || null,
        brand: selectedSug?.brand || null, packSize: selectedSug?.packSize || null,
      });
      setName(''); setSelectedSug(null); verRef.current = -1; loadItems(openId);
    } catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); } finally { setAdding(false); }
  };
  const quickAdd = async (p) => {
    if (!openId) return;
    try {
      await axiosInstance.post(`/price/lists/${openId}/items`, {
        name: p.name, qty: 1, normalizedName: p.normalizedName || null,
        brand: p.brand || null, packSize: p.packSize || null,
      });
      verRef.current = -1; loadItems(openId);
      toast.success(t('price.addedToListToast'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); }
  };
  const patchItem = async (id, body) => {
    try { await axiosInstance.patch(`/price/lists/${openId}/items/${id}`, body); verRef.current = -1; loadItems(openId); }
    catch (e) { toast.error(e?.response?.data?.detail || t('price.errGeneric')); }
  };
  const setQty = (id, qty) => { if (qty < 1) return; setItems((l) => l.map((x) => (x.id === id ? { ...x, qty } : x))); patchItem(id, { qty }); };
  const toggle = (id, checked) => patchItem(id, { checked });
  const remove = async (id) => { try { await axiosInstance.delete(`/price/lists/${openId}/items/${id}`); verRef.current = -1; loadItems(openId); } catch { toast.error(t('price.errGeneric')); } };
  const clearDone = async () => { try { await axiosInstance.post(`/price/lists/${openId}/clear-checked`); verRef.current = -1; loadItems(openId); } catch { toast.error(t('price.errGeneric')); } };

  const createList = async () => {
    const nm = newName.trim();
    if (!nm) { toast.error(t('listShare.nameRequired', 'Please name the list')); return; }
    const body = { kind: newMode, name: nm };
    if (newMode === 'calendar') { if (!newCal) { toast.error(t('listShare.pickCalendar', 'Choose a calendar')); return; } body.calendarId = newCal; }
    try {
      const r = await axiosInstance.post('/price/lists', body);
      await loadLists();
      setShowCreate(false); setNewName(''); setNewMode('personal'); setNewCal('');
      setOpenId(r.data.id);
      toast.success(t('listShare.created', 'List created'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const copyToToday = async (allItems) => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!allItems && ids.length === 0) { toast.error(t('listShare.pickItems', 'Tick the items you need')); return; }
    try {
      const r = await axiosInstance.post(`/price/lists/${openId}/copy-to-today`, allItems ? {} : { itemIds: ids });
      const n = (r.data?.added || 0) + (r.data?.merged || 0);
      toast.success(t('listShare.copiedToday', { n, defaultValue: "Added {{n}} to Today's Shopping" }));
      setSelected({});
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const doShare = async () => {
    if (!shareEmail.trim()) return;
    try {
      await axiosInstance.post(`/price/lists/${openId}/share`, { email: shareEmail.trim() });
      toast.success(t('listShare.shared', 'List shared'));
      setShareEmail('');
      await loadLists();
      loadMembers();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const removeMember = async (uid) => {
    try {
      await axiosInstance.post(`/price/lists/${openId}/unshare`, { userId: uid });
      await loadLists();
      loadMembers();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const copySelectedToList = async (targetId, label) => {
    setListPickOpen(false);
    const chosen = active.filter((it) => selected[it.id]);
    if (chosen.length === 0) return;
    const res = await addItemsToListApi(targetId, chosen.map((it) => ({ name: it.name, normalizedName: it.normalizedName, brand: it.brand, packSize: it.packSize, qty: it.qty || 1 })));
    toast.success(t('listShare.addedToList', { n: res.added + res.merged, list: label, defaultValue: 'Added {{n}} to {{list}}' }));
    setSelected({});
  };
  const renameList = async () => {
    const nm = window.prompt(t('listShare.rename', 'Rename list'), openList?.name);
    if (!nm || !nm.trim()) return;
    try { await axiosInstance.patch(`/price/lists/${openId}`, { name: nm.trim() }); loadLists(); } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const deleteList = async () => {
    if (!window.confirm(t('listShare.delListConfirm', 'Delete this list? Items on it will be removed.'))) return;
    try { await axiosInstance.delete(`/price/lists/${openId}`); setOpenId(null); loadLists(); } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const openRepeat = async () => {
    setShowRepeat(true); setLastShop(null); setRepeatSel({});
    try { const r = await axiosInstance.get('/price/last-shop'); setLastShop(r.data || { receipt: null, items: [] }); }
    catch { setLastShop({ receipt: null, items: [] }); }
  };
  const addRepeat = async (all) => {
    const items = (lastShop?.items || []).filter((_, i) => all || repeatSel[i]);
    if (items.length === 0) { toast.error(t('listShare.pickItems', 'Tick the items you need')); return; }
    try {
      const r = await axiosInstance.post('/price/add-items-to-today', { items });
      const n = (r.data?.added || 0) + (r.data?.merged || 0);
      toast.success(t('listShare.copiedToday', { n, defaultValue: "Added {{n}} to Today's Shopping" }));
      setShowRepeat(false); loadLists();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  if (!user) return <SignInPrompt label={t('price.slSignIn')} />;

  // ------------------------------- HOME -------------------------------
  if (!openId) {
    return (
      <div className="space-y-4" data-testid="price-shoppinglist">
        <button onClick={() => todayList && setOpenId(todayList.id)} disabled={!todayList}
          className="w-full text-start rounded-2xl bg-emerald-600 text-white p-4 flex items-center gap-3 hover:bg-emerald-700 transition-colors disabled:opacity-60" data-testid="open-today">
          <ShoppingCart className="h-6 w-6 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold">{t('listShare.today', "Today's Shopping")}</p>
            <p className="text-[12px] opacity-90">{t('listShare.todayHint', 'Your current shopping trip · compare prices')}{todayList ? ` · ${t('listShare.itemsN', { n: todayList.itemCount || 0, defaultValue: '{{n}} items' })}` : ''}</p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0" />
        </button>

        {/* Repeat Last Shop */}
        <button onClick={openRepeat} className="w-full text-start rounded-xl bg-white border p-3 flex items-center gap-3 hover:bg-slate-50 transition-colors" data-testid="repeat-last-shop">
          <RotateCcw className="h-5 w-5 text-indigo-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900">{t('listShare.repeatShop', 'Repeat Last Shop')}</p>
            <p className="text-[11px] text-gray-400">{t('listShare.repeatHint', 'Add items from your most recent receipt')}</p>
          </div>
          <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />
        </button>

        {showRepeat && (
          <div className="rounded-2xl bg-white border p-4 space-y-3" data-testid="repeat-panel">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">{t('listShare.repeatShop', 'Repeat Last Shop')}</p>
              <button onClick={() => setShowRepeat(false)} className="text-gray-400 hover:text-gray-700" data-testid="repeat-close"><X className="h-4 w-4" /></button>
            </div>
            {!lastShop ? (
              <div className="text-center py-4"><Loader2 className="h-5 w-5 animate-spin text-indigo-600 mx-auto" /></div>
            ) : (lastShop.items || []).length === 0 ? (
              <p className="text-[13px] text-gray-400 py-3 text-center" data-testid="repeat-empty">{t('listShare.repeatEmpty', 'No previous shop found. Scan a receipt first.')}</p>
            ) : (
              <>
                {lastShop.receipt && <p className="text-[12px] text-gray-500">{lastShop.receipt.shopName}{lastShop.receipt.receiptDate ? ` · ${lastShop.receipt.receiptDate}` : ''}</p>}
                <div className="max-h-56 overflow-auto space-y-1">
                  {lastShop.items.map((it, i) => (
                    <label key={i} className="flex items-center gap-2 py-1 text-sm" data-testid={`repeat-item-${i}`}>
                      <input type="checkbox" checked={!!repeatSel[i]} onChange={(e) => setRepeatSel((s) => ({ ...s, [i]: e.target.checked }))} className="h-4 w-4" data-testid={`repeat-check-${i}`} />
                      <span className="truncate flex-1">{it.name}{it.packSize ? ` · ${it.packSize}` : ''}{it.qty > 1 ? ` ×${it.qty}` : ''}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 h-9 text-[13px]" onClick={() => addRepeat(false)} data-testid="repeat-add-selected">{t('listShare.addSelectedShort', 'Add selected')}</Button>
                  <Button variant="outline" className="rounded-full h-9 text-[13px]" onClick={() => addRepeat(true)} data-testid="repeat-add-all">{t('listShare.addWholeShop', 'Add whole shop')}</Button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-800 px-1">{t('listShare.myLists', 'My Lists')}</p>
          {otherLists.length === 0 && <p className="text-[13px] text-gray-400 px-1" data-testid="lists-empty">{t('listShare.noLists', 'Create lists for anything — groceries, DIY, logs, pets, gardening…')}</p>}
          {otherLists.map((l) => {
            const badge = KIND_BADGE[l.kind] || KIND_BADGE.personal;
            return (
              <button key={l.id} onClick={() => setOpenId(l.id)} className="w-full text-start rounded-xl bg-white border p-3 flex items-center gap-3 hover:bg-slate-50 transition-colors" data-testid={`open-list-${l.id}`}>
                <ShoppingBag className="h-5 w-5 text-indigo-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{l.name}</p>
                  <p className="text-[11px] text-gray-400">{t('listShare.itemsN', { n: l.itemCount || 0, defaultValue: '{{n}} items' })}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${badge.cls}`} data-testid={`list-badge-${l.id}`}>{t(`listShare.kind_${l.kind}`, badge.label)}</span>
                <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />
              </button>
            );
          })}
          <button onClick={() => setShowCreate((s) => !s)} className="w-full rounded-xl border-2 border-dashed border-indigo-200 text-indigo-600 p-3 text-sm font-medium inline-flex items-center justify-center gap-1 hover:bg-indigo-50 transition-colors" data-testid="new-list-btn">
            <Plus className="h-4 w-4" />{t('listShare.newList', 'New List')}
          </button>
        </div>

        {showCreate && (
          <div className="rounded-2xl bg-white border p-4 space-y-3" data-testid="new-list-form">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('listShare.namePh', 'List name (e.g. Weekly Groceries, DIY, Logs)')} className="h-10 text-sm" data-testid="new-list-name" />
            <div className="grid grid-cols-3 gap-1.5 text-[12px]">
              {[['personal', t('listShare.kind_personal', 'Personal')], ['calendar', t('listShare.kind_calendar', 'Household')], ['direct', t('listShare.kind_direct', 'Shared')]].map(([k, label]) => (
                <button key={k} onClick={() => setNewMode(k)} className={`rounded-full py-2 border transition-colors ${newMode === k ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600'}`} data-testid={`new-list-mode-${k}`}>{label}</button>
              ))}
            </div>
            {newMode === 'calendar' && (
              <select value={newCal} onChange={(e) => setNewCal(e.target.value)} className="w-full h-10 px-3 rounded-xl border text-sm bg-white" data-testid="new-list-calendar">
                <option value="">{t('listShare.pickCalendar', 'Choose a calendar…')}</option>
                {cals.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <p className="text-[11px] text-gray-400">{newMode === 'personal' ? t('listShare.hintPersonal', 'Private to you.') : newMode === 'calendar' ? t('listShare.hintCalendar', 'Shared with everyone on that calendar.') : t('listShare.hintDirect', 'Share with specific people by email.')}</p>
            <div className="flex gap-2">
              <Button className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={createList} data-testid="new-list-create">{t('listShare.create', 'Create')}</Button>
              <Button variant="outline" className="rounded-full" onClick={() => setShowCreate(false)}>{t('price.back', 'Back')}</Button>
            </div>
          </div>
        )}

        {onManageProducts && (
          <button onClick={onManageProducts} className="w-full text-center text-[13px] text-gray-500 underline underline-offset-2 py-2 hover:text-indigo-600 transition-colors" data-testid="sl-manage-products">
            {t('price.manageProductsLink')}
          </button>
        )}
      </div>
    );
  }

  // ------------------------------ DETAIL ------------------------------
  const selCount = Object.keys(selected).filter((k) => selected[k]).length;
  const dbadge = openList ? (KIND_BADGE[openList.kind] || KIND_BADGE.personal) : KIND_BADGE.personal;

  return (
    <div className="space-y-4" data-testid="price-shoppinglist-detail">
      {/* header */}
      <div className="flex items-center gap-2">
        <button onClick={() => setOpenId(null)} className="h-9 w-9 rounded-full bg-white border flex items-center justify-center shrink-0" aria-label={t('price.back', 'Back')} data-testid="list-back"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-gray-900 truncate" data-testid="list-title">{isTodayOpen ? t('listShare.today', "Today's Shopping") : openList?.name}</p>
          {!isTodayOpen && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${dbadge.cls}`}>{t(`listShare.kind_${openList?.kind}`, dbadge.label)}</span>}
        </div>
        {!isTodayOpen && openList?.isOwner && (
          <>
            <button onClick={renameList} className="text-gray-400 hover:text-indigo-600 p-1.5" aria-label={t('listShare.rename', 'Rename')} data-testid="list-rename"><Pencil className="h-4 w-4" /></button>
            <button onClick={deleteList} className="text-gray-400 hover:text-rose-600 p-1.5" aria-label={t('price.delete', 'Delete')} data-testid="list-delete"><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>

      {/* direct-share controls — available to the OWNER of any shareable list:
          normal named Personal lists + existing Shared lists. Never shown on
          Today's Shopping or Household/calendar lists (they keep their own model). */}
      {openList?.isOwner && !isTodayOpen && openList?.kind !== 'calendar' && (
        <div className="rounded-2xl bg-white border p-3 space-y-2" data-testid="list-share-controls">
          <button onClick={() => setShowShare((s) => !s)} className="text-sm text-indigo-700 inline-flex items-center gap-1" data-testid="list-share-toggle"><Users className="h-4 w-4" />{t('listShare.share', 'Share with someone')}</button>
          {showShare && (
            <div className="space-y-2" data-testid="list-share-box">
              <p className="text-[12px] text-gray-500 leading-snug" data-testid="list-share-help">{t('listShare.shareHelp', 'People you invite can view and edit this list, and can see the names and email addresses of the others sharing it.')}</p>
              <div className="flex gap-2">
                <Input value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder={t('listShare.emailPh', "Find-2-Day user's email")} className="h-9 text-sm" data-testid="list-share-email" />
                <Button className="rounded-full bg-indigo-600 h-9" onClick={doShare} data-testid="list-share-submit">{t('listShare.invite', 'Invite')}</Button>
              </div>
            </div>
          )}
          {members.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid="list-members">
              {members.map((m) => (
                <span key={m.userId} className="inline-flex items-center gap-1 text-[11px] bg-gray-100 text-gray-700 rounded-full pl-2 pr-1 py-1" data-testid={`list-member-${m.userId}`}>
                  {m.name || m.email}
                  <button onClick={() => removeMember(m.userId)} className="hover:text-rose-600" aria-label={t('listShare.remove', 'Remove')} data-testid={`list-member-remove-${m.userId}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {openList && openList.kind !== 'personal' && (
        <p className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1 inline-block" data-testid="list-shared-badge">{t('listShare.editingShared', 'You are working on a shared list')}</p>
      )}

      {/* add product */}
      {canEdit && (
        <div className="rounded-2xl bg-white border p-4 space-y-3">
          <p className={isTodayOpen ? 'text-lg font-bold text-gray-900' : 'text-sm font-semibold text-gray-800'}>{isTodayOpen ? t('price.slAddHeading') : t('price.slAddTitle')}</p>
          <div className="flex gap-2">
            <div className="flex-1">
              <ProductSuggestInput value={name} onChange={(v) => { setName(v); setSelectedSug(null); }} onSelect={(s) => { setName(s.name); setSelectedSug(s); }} onEnter={add} placeholder={t('price.slNamePh')} testid="sl-name-input" />
            </div>
            <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700 h-11 px-5" onClick={add} disabled={adding} data-testid="sl-add-button">
              {adding ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
            </Button>
          </div>
          {/* Quick add: things you buy often — one tap adds to this list */}
          {isTodayOpen && regulars.length > 0 && (
            <div className="pt-1" data-testid="sl-quick-add">
              <p className="text-[12px] font-semibold text-gray-400 mb-1.5">{t('price.quickAddTitle')}</p>
              <div className="flex flex-wrap gap-2">
                {regulars.map((p, i) => (
                  <button key={i} onClick={() => quickAdd(p)} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 px-3 py-2 text-sm font-medium hover:bg-indigo-100 transition-colors" data-testid={`sl-quick-add-${i}`}>
                    <Plus className="h-4 w-4" />{p.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto" /></div>
      ) : isTodayOpen ? (
        <>
          {active.length > 0 && <CompareBasket hasItems={active.length > 0} terms={active.map((it) => it.name)} listId={openId} />}
          {active.length > 0 && <SponsoredStrip terms={active.map((it) => it.name)} testid="shoppinglist-sponsored" />}
          {active.length > 0 && selCount > 0 && canEdit && (
            <div className="flex" data-testid="sl-copy-bar">
              <Button className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700 h-10 text-[13px]" onClick={() => setListPickOpen(true)} data-testid="sl-copy-to-list">
                <ListPlus className="h-4 w-4 mr-1.5" />{t('listShare.copySelectedToList', { n: selCount, defaultValue: 'Copy to list ({{n}})' })}
              </Button>
            </div>
          )}
          {active.length === 0 && completed.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8" data-testid="sl-empty">{t('price.slEmpty')}</p>
          ) : (
            <div className="space-y-2" data-testid="sl-active">
              {activeAZ.map((it) => (
                <div key={it.id} className="rounded-xl bg-white border p-3 flex items-center gap-2" data-testid={`sl-item-${it.id}`}>
                  <button onClick={() => setSelected((s) => ({ ...s, [it.id]: !s[it.id] }))} className="shrink-0 text-indigo-600" aria-label={t('listShare.select', 'Select')} data-testid={`sl-select-${it.id}`}>
                    {selected[it.id] ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-gray-300" />}
                  </button>
                  <button onClick={() => toggle(it.id, true)} disabled={!canEdit} className="h-6 w-6 rounded-full border-2 border-gray-300 hover:border-emerald-500 shrink-0" aria-label={t('price.slTick')} data-testid={`sl-tick-${it.id}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900 truncate" data-testid={`sl-name-${it.id}`}>{it.name}</p>
                    {(it.brand || it.packSize) && <p className="text-[11px] text-gray-400 truncate">{[it.brand, it.packSize].filter(Boolean).join(' · ')}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setQty(it.id, it.qty - 1)} className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center" data-testid={`sl-minus-${it.id}`}><Minus className="h-3 w-3" /></button>
                      <span className="w-6 text-center text-sm font-semibold" data-testid={`sl-qty-${it.id}`}>{it.qty}</span>
                      <button onClick={() => setQty(it.id, it.qty + 1)} className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center" data-testid={`sl-plus-${it.id}`}><Plus className="h-3 w-3" /></button>
                      <button onClick={() => remove(it.id)} className="text-gray-400 hover:text-red-600 p-1 ml-1" data-testid={`sl-remove-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {completed.length > 0 && (
            <div className="space-y-2" data-testid="sl-completed">
              <div className="flex items-center justify-between">
                <p className="text-[12px] font-semibold text-gray-400 flex items-center gap-1"><PackageCheck className="h-3.5 w-3.5" />{t('price.slBought', { n: completed.length })}</p>
                {canEdit && <button onClick={clearDone} className="text-[12px] text-indigo-600 font-medium" data-testid="sl-clear-completed">{t('price.slClearCompleted')}</button>}
              </div>
              {completedAZ.map((it) => (
                <div key={it.id} className="rounded-xl bg-slate-50 border border-gray-100 p-2.5 flex items-center gap-2 opacity-70" data-testid={`sl-done-${it.id}`}>
                  <button onClick={() => toggle(it.id, false)} disabled={!canEdit} className="h-6 w-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0" aria-label={t('price.slUntick')} data-testid={`sl-untick-${it.id}`}><Check className="h-3.5 w-3.5" /></button>
                  <p className="text-sm text-gray-500 line-through truncate flex-1">{it.name}{it.qty > 1 ? ` ×${it.qty}` : ''}</p>
                  {canEdit && <button onClick={() => remove(it.id)} className="text-gray-400 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* NAMED LIST: pick items to add to Today's Shopping + Compare This List */
        <>
          {active.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8" data-testid="sl-empty">{t('listShare.listEmpty', 'No products yet. Add anything you buy.')}</p>
          ) : (
            <>
              {canEdit && (
                <div className="flex flex-wrap gap-2" data-testid="named-list-actions">
                  <Button className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 h-10 text-[13px]" onClick={() => copyToToday(false)} disabled={selCount === 0} data-testid="add-selected-today">
                    <ListPlus className="h-4 w-4 mr-1.5" />{t('listShare.addSelected', { n: selCount, defaultValue: 'Add selected ({{n}})' })}
                  </Button>
                  <Button variant="outline" className="rounded-full h-10 text-[13px]" onClick={() => copyToToday(true)} data-testid="add-all-today">
                    {t('listShare.addAll', 'Add whole list')}
                  </Button>
                </div>
              )}
              <CompareBasket hasItems={active.length > 0} terms={active.map((it) => it.name)} listId={openId} label={t('listShare.compareThis', 'Compare This List')} />
              <div className="space-y-2" data-testid="named-list-items">
                {active.map((it) => {
                  const on = !!selected[it.id];
                  return (
                    <div key={it.id} className="rounded-xl bg-white border p-3 flex items-center gap-2" data-testid={`nl-item-${it.id}`}>
                      <button onClick={() => setSelected((s) => ({ ...s, [it.id]: !on }))} className="shrink-0 text-indigo-600" aria-label={t('listShare.select', 'Select')} data-testid={`nl-select-${it.id}`}>
                        {on ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-gray-300" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900 truncate" data-testid={`nl-name-${it.id}`}>{it.name}</p>
                        {(it.brand || it.packSize) && <p className="text-[11px] text-gray-400 truncate">{[it.brand, it.packSize].filter(Boolean).join(' · ')}</p>}
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => setQty(it.id, it.qty - 1)} className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center" data-testid={`nl-minus-${it.id}`}><Minus className="h-3 w-3" /></button>
                          <span className="w-6 text-center text-sm font-semibold" data-testid={`nl-qty-${it.id}`}>{it.qty}</span>
                          <button onClick={() => setQty(it.id, it.qty + 1)} className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center" data-testid={`nl-plus-${it.id}`}><Plus className="h-3 w-3" /></button>
                          <button onClick={() => remove(it.id)} className="text-gray-400 hover:text-red-600 p-1 ml-1" data-testid={`nl-remove-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
      <ListPickerSheet open={listPickOpen} onClose={() => setListPickOpen(false)} onPick={copySelectedToList} excludeId={openId} />
    </div>
  );
};
